import { useMemo, useState } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { usePageMeta } from "@/hooks/usePageMeta";
import { faqs, feedItems, siteBrand, statusMeta, type FeedStatus } from "@/mocks/news";
import HeroSection from "./components/HeroSection";
import LiveTicker from "./components/LiveTicker";
import TopicBand from "./components/TopicBand";
import FeedSection from "./components/FeedSection";
import HotRanking from "./components/HotRanking";
import StatusLegend from "./components/StatusLegend";
import FaqSection from "./components/FaqSection";
import AboutSection from "./components/AboutSection";
import SubscribeSection from "./components/SubscribeSection";
import BackToTop from "./components/BackToTop";

const CANONICAL = "https://hzduiyou03.cn/";
const OG_IMAGE =
  "https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20of%20warm%20spotlights%20and%20broadcast%20signal%20waves%20radiating%20over%20cream%20and%20vermilion%20color%20blocks%2C%20layered%20paper%20cut%20style%2C%20bold%20minimal%20geometric%20shapes%2C%20energetic%20but%20clean%20composition%2C%20no%20text%2C%20warm%20high%20contrast%20palette&width=900&height=1120&seq=hero-newsroom-01&orientation=portrait";

export default function Home() {
  const [activeStatus, setActiveStatus] = useState<FeedStatus | "全部">("全部");
  const [activeTopic, setActiveTopic] = useState("");
  const [query, setQuery] = useState("");

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: siteBrand.name,
        url: CANONICAL,
        description: siteBrand.intro,
        inLanguage: "zh-CN",
        potentialAction: {
          "@type": "SearchAction",
          target: `${CANONICAL}?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: siteBrand.name,
        url: CANONICAL,
        email: siteBrand.email,
        description: siteBrand.slogan,
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "实时滚动时间线",
        description: "按时间顺序整理的娱乐热点进展列表",
        itemListElement: feedItems.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.title,
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ],
    [],
  );

  usePageMeta({
    title: "黑料今日 · 娱乐热点实时滚动与突发爆料进展追踪",
    description:
      "黑料今日是实时滚动的娱乐热点资讯站，第一时间整理黑料今日实时爆料、突发进展与即时消息，标注信息状态，支持按分类与关键词筛选，跟得上正在热聊的那一条。",
    keywords: "黑料今日,黑料今日实时爆料,黑料今日突发,娱乐热点,即时消息追踪",
    canonical: CANONICAL,
    ogImage: OG_IMAGE,
    jsonLd,
  });

  const handleSelectTopic = (topic: string) => {
    setActiveTopic(topic);
    if (topic) {
      document.getElementById("live")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <HeroSection />
        <LiveTicker />

        <TopicBand activeTopic={activeTopic} onSelect={handleSelectTopic} />

        <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-8 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8">
            <FeedSection
              activeStatus={activeStatus}
              onStatusChange={setActiveStatus}
              activeTopic={activeTopic}
              onClearTopic={() => setActiveTopic("")}
              query={query}
              onQueryChange={setQuery}
            />
            {activeStatus !== "全部" && (
              <p className="mt-6 rounded-md border border-background-200 bg-background-100/70 px-4 py-3 text-xs text-foreground-600">
                当前展示「{activeStatus}」状态的内容：{statusMeta[activeStatus].desc}。
              </p>
            )}
          </div>

          <aside className="w-full space-y-6 lg:col-span-4">
            <HotRanking />
            <StatusLegend />
          </aside>
        </div>

        <FaqSection />
        <AboutSection />
        <SubscribeSection />
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}